import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, CardContent, TextField, Button, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeFilter from '../DateRangeFilter';
import { CATEGORY_CHOICES, CURRENCY_CHOICES, STATUS_CHOICES } from '../../utils/incomeUtils';

const IncomeFilters = ({
  searchTerm,
  categoryFilter,
  currencyFilter,
  statusFilter,
  dateRange,
  filterOptions,
  onSearchChange,
  onCategoryFilterChange,
  onCurrencyFilterChange,
  onStatusFilterChange,
  onDateRangeChange,
  onClearFilters,
}) => {
  // Use dynamic filter options or fallback to constants
  const categoryOptions =
    filterOptions?.categories?.map((cat) => ({ value: cat, label: cat })) ||
    CATEGORY_CHOICES.map((cat) => ({ value: cat, label: cat }));
  const currencyOptions =
    filterOptions?.currencies?.map((curr) => ({ value: curr, label: curr })) ||
    CURRENCY_CHOICES.map((curr) => ({ value: curr, label: curr }));
  const statusOptions =
    filterOptions?.statuses?.map((status) => ({ value: status, label: status })) ||
    STATUS_CHOICES.map((status) => ({ value: status, label: status }));

  return (
    <Grid item xs={12}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                placeholder="Search incomes..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={{ value: categoryFilter, label: categoryFilter }}
                onChange={(event, newValue) => onCategoryFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...categoryOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Category" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={{ value: currencyFilter, label: currencyFilter }}
                onChange={(event, newValue) => onCurrencyFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...currencyOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Currency" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                value={{ value: statusFilter, label: statusFilter }}
                onChange={(event, newValue) => onStatusFilterChange(newValue?.value || 'All')}
                options={[{ value: 'All', label: 'All' }, ...statusOptions]}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6} md={dateRange.preset === 'custom' ? 4 : 1.7}>
              <DateRangeFilter
                onDateRangeChange={onDateRangeChange}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                selectedPreset={dateRange.preset}
                sx={{ height: '40px' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px' }}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

IncomeFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  categoryFilter: PropTypes.string.isRequired,
  currencyFilter: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    currencies: PropTypes.arrayOf(PropTypes.string),
    statuses: PropTypes.arrayOf(PropTypes.string),
  }),
  onSearchChange: PropTypes.func.isRequired,
  onCategoryFilterChange: PropTypes.func.isRequired,
  onCurrencyFilterChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

IncomeFilters.defaultProps = {
  filterOptions: {},
};

export default IncomeFilters;
